import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { LlmProviderViewStore } from "../../store";
import { LlmProviderView } from "../view/LlmProviderView";
import { LlmProviderUpdateStore } from "../../store";
import { LlmProviderUpdate } from "../update/LlmProviderUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type LlmProviderModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const LlmProviderModalContent: React.FC<LlmProviderModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new LlmProviderUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new LlmProviderViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <LlmProviderView
            llmProviderViewStore={storeInfo.store as LlmProviderViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <LlmProviderUpdate
            llmProviderUpdateStore={storeInfo.store as LlmProviderUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
